import { defineComponent } from "vue";
import MessageAlert from "@/components/message-alert";
import { FormKit } from "@formkit/vue";
import * as Sentry from "@sentry/vue";

const FALLBACK_ERR_MESSAGE =
  "Nastala neočekávaná chyba. Prosím, kontaktujte naši Zákaznickou linku na čísle 730 430 430.";
const FORM_NAME = "sim order form: new client";
const CUSTOMER_TYPE = "new";

export default defineComponent({
  template: `
      <div class="order-form order-step-1" v-if="!this.stepOne.isSubmitted">

      <FormKit
          type="form"
          @submit="handleSubmitStepOne"
          :form-class="this.stepOne.isSubmitted ? 'hide' : 'show'"
          :submit-label="'Ozvěte se mi'"
      >
        <MessageAlert v-if="this.stepOne.errorMessage" type="error">
          <span>{{ this.stepOne.errorMessage }}</span>
        </MessageAlert>

        <div>
          <FormKit
              type="text"
              name="phone_no_contact"
              id="phone_no_contact"
              label="Telefonní číslo pro kontakt"
              v-model="this.phoneNoContact"
              validation="required"
              @focusout="handleFocusOut"
              :sections-schema="{
            prefix: {
              children: [
                {
                  $el: 'span',
                  attrs: {
                    style: 'margin-left: 0.5rem;',
                  },
                  // eslint-disable-next-line no-irregular-whitespace
                  children: '+420',
                },
              ],
            },
          }"
          />
        </div>
      </FormKit>

      <h6 class="mb-2">Vše vyřídíte z domova za pár minut</h6>

      <ol style="margin: 0; padding-left: 20px;">
        <li class="mb-1">Zanechte nám kontakt a my se vám co nejdříve ozveme.</li>
        <li class="mb-1">Zodpovíme vaše dotazy a v případě zájmu společně dokončíme objednávku.</li>
        <li class="mb-1">Operátorovi nahlásíte, zda chcete přenést své současné číslo nebo vám máme vytvořit nové.</li>
        <li class="mb-1">Zašleme vám přihlašovací údaje do Klientské zóny, kde na klik podepíšete smlouvu.</li>
        <li class="mb-1">Nová SIM vám do několika dní dorazí poštou. Službu IPTV budete mít dostupnou ihned.</li>
      </ol>

      </div>
      <div class="order-form order-step-final" v-if="this.stepOne.isSubmitted">
      <MessageAlert
          v-if="!this.stepOne.isPassed && this.stepOne.errorMessage === null"
          type="info"
      >
        Vaše objednávka se zpracovává. Moment strpení, prosím.
      </MessageAlert>
      <MessageAlert v-if="this.stepOne.isPassed" type="success">
      <span
      >Vaše objednávka byla úspěšně zpracována. Brzy se Vám ozveme.
        Děkujeme!</span
      >
      </MessageAlert>
      <MessageAlert
          v-if="!this.stepOne.isPassed && this.stepOne.errorMessage !== null"
          type="error"
      >
      <span>
        {{
          this.stepOne.errorMessage ??
          "Nastala neočekávaná chyba. Prosím, kontaktujte naši Zákaznickou linku na čísle 730 430 430."
        }}
      </span>
      </MessageAlert>
      </div>
      <slot></slot>
    `,
  name: "OrderFormNewClient",
  props: {
    program: {
      type: Object,
      required: true,
    },
    includeIptv: {
      type: Boolean,
      default: false,
      required: false,
    },
    iptvPrice: {
      type: Number,
      default: 0,
      required: false,
    },
    gtmEventPrefix: {
      type: String,
      required: false,
      default: "sim_order_form_",
    },
  },
  data() {
    return {
      clientId: null,
      verificationInstanceId: null,
      verificationCode: null,
      phoneNoContact: null,
      // Steps
      stepOne: {
        isSubmitted: false,
        isPassed: false,
        errorMessage: null,
      },
    };
  },
  emits: ["stepChanged"],
  async mounted() {
    this.$emit("stepChanged", {
      current: 1,
      total: 1,
    });
    this.$store.direct.getters.exponeaService.track("order_form", {
      form_name: FORM_NAME,
      form_state: "opened",
      form_step: 1,
      url: window.location.href,
      selected_tariff_id: this.program.program_id,
      selected_tariff_name: this.getTariffName(),
      selected_tariff_technology: this.getTariffTechnology(),
    });

    this.$store.direct.getters.gtmService.track(this.gtmEventPrefix + "open", {
      existing_customer: CUSTOMER_TYPE,
      sim_tariff: this.getTariffName(),
      technology: this.getTariffTechnology(),
    });

    await this.$store.direct.getters.recaptchaService.init();
  },
  computed: {
    phoneNoContactTrimmed() {
      return "+420" + this.phoneNoContact.replace(/\s/g, "");
    },
  },
  components: {
    MessageAlert,
    FormKit,
  },
  methods: {
    getTariffTechnology(): string {
      if (this.includeIptv) return "iptv+sim";
      return "sim";
    },
    getTariffName(): string {
      if (this.includeIptv) return "IPTV + " + this.program.name;
      return this.program.name;
    },
    async handleFocusOut(payload: FocusEvent) {
      await this.$store.direct.getters.exponeaService.track("order_form", {
        form_name: FORM_NAME,
        form_state: "edited",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        field_key: payload.target?.name,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        field_value: payload.target?._value,
        url: window.location.href,
        selected_tariff_id: this.program.program_id,
        selected_tariff_name: this.getTariffName(),
        selected_tariff_technology: this.getTariffTechnology(),
      });
    },
    async handleTransferNumberChange() {
      await this.$store.direct.getters.exponeaService.track("order_form", {
        form_name: FORM_NAME,
        form_state: "edited",
        field_key: "transfer_number",
        field_value: this.phoneNoContact,
        url: window.location.href,
        selected_tariff_id: this.program.program_id,
        selected_tariff_name: this.getTariffName(),
        selected_tariff_technology: this.getTariffTechnology(),
      });
    },
    async handleSubmitStepOne() {
      try {
        const podaAuthTrack = this.$store.direct.getters.cookieService.getCookieOne("poda_auth_track");
        this.stepOne.errorMessage = null;
        this.stepOne.isSubmitted = true;
        const captcha = await this.$store.direct.getters.recaptchaService.getToken("order");
        const webApiResponse = await this.$store.direct.getters.webApiService.createWebOrder({
          tel_num: this.phoneNoContactTrimmed,
          order_source: "web",
          tariff_id: this.program.program_id,
          tariff_name: this.getTariffName(),
          tariff_technology: this.getTariffTechnology(),
          customer_type: "b2c",
          lead_info: this.$store.direct.getters.leadInfoService.getLeadInfo(),
          session_storage_id: this.$store.direct.getters.sessionStorageTagger.getTag(),
          persistent_storage_id: this.$store.direct.getters.localStorageTagger.getTag(),
          captcha_score: "1",
          is_employee: podaAuthTrack !== "" && podaAuthTrack !== null,
          captcha: captcha,
        });
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "submitted",
          form_step: 1,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        await this.$store.direct.getters.gtmService.track(this.gtmEventPrefix + "submit", {
          web_order_uuid: webApiResponse?.uuid ?? null,
          existing_customer: CUSTOMER_TYPE,
          sim_tariff: this.getTariffName(),
          technology: this.getTariffTechnology(),
        });
        if (typeof sessionStorage !== undefined && typeof sessionStorage.setItem === "function") {
          sessionStorage.setItem("OrderFormNewClient.stepOne.lastSubmittedAt", new Date().getTime().toString());
        }
        this.stepOne.isPassed = true;
      } catch (error) {
        this.stepOne.errorMessage = FALLBACK_ERR_MESSAGE;

        Sentry.captureException(error);

        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "submit_failed",
          error_message: FALLBACK_ERR_MESSAGE,
          form_step: 1,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });

        await this.$store.direct.getters.gtmService.track(this.gtmEventPrefix + "submit_failed", {
          existing_customer: CUSTOMER_TYPE,
          sim_tariff: this.getTariffName(),
          technology: this.getTariffTechnology(),
        });
        this.stepOne.isPassed = false;
      }
    },
  },
});
